import { getAuth } from '../libs/cognito-api/auth/auth';
import { setAuthToken } from '../libs/cognito-api/mutator';
import {
  getToken, setToken,
  getRefreshToken, setRefreshToken,
  getExpiresAt, setExpiresAt,
  getEmail, setEmail,
  clearSessionTokens,
} from './storage';

const { postAuthLogin, postAuthLoginRefreshToken } = getAuth();

// Refresh token 5 minutes before expiry
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

interface TokenPayload {
  bearerToken?: string | null;
  refreshToken?: string | null;
  expiresAt?: string | null;
}

async function persistTokens(payload: TokenPayload): Promise<void> {
  const ops: Promise<void>[] = [];
  if (payload.bearerToken) ops.push(setToken(payload.bearerToken));
  if (payload.refreshToken) ops.push(setRefreshToken(payload.refreshToken));
  if (payload.expiresAt) ops.push(setExpiresAt(payload.expiresAt));
  await Promise.all(ops);
}

export async function login(email: string, password: string): Promise<void> {
  const response = await postAuthLogin({ email, password });

  const tokenData = response.data;
  if (!response.status || !tokenData?.bearerToken) {
    throw new Error(response.message || 'Credenciais inválidas.');
  }

  await Promise.all([
    persistTokens(tokenData),
    setEmail(email),
  ]);

  setAuthToken(tokenData.bearerToken);
}

export async function logout(): Promise<void> {
  setAuthToken(null);
  await clearSessionTokens();
}

/**
 * Returns a valid bearer token, auto-refreshing if within REFRESH_MARGIN_MS of expiry.
 * Returns null if the user is not logged in or the session cannot be refreshed.
 */
export async function getValidToken(): Promise<string | null> {
  const [token, refreshToken, expiresAt] = await Promise.all([
    getToken(),
    getRefreshToken(),
    getExpiresAt(),
  ]);

  if (!token) return null;

  if (expiresAt) {
    const expiryMs = new Date(expiresAt).getTime();
    if (Date.now() >= expiryMs - REFRESH_MARGIN_MS) {
      if (refreshToken) {
        try {
          const r = await postAuthLoginRefreshToken({ refreshToken });
          if (r.status && r.data?.bearerToken) {
            await persistTokens(r.data);
            setAuthToken(r.data.bearerToken);
            return r.data.bearerToken;
          }
        } catch {
          // Refresh failed — clear session and force re-login
        }
      }
      await clearSessionTokens();
      return null;
    }
  }

  setAuthToken(token);
  return token;
}

export async function getLoggedInEmail(): Promise<string | null> {
  return getEmail();
}

export async function isLoggedIn(): Promise<boolean> {
  return !!(await getToken());
}
